import { useEffect } from "react";
import { get, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

import { useRequestFormReviewValidation } from "@RequestFormV2/context/reviewValidation";
import type { HandlingUnitHashMap, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { isPackingListRowEmpty } from "@RequestFormV2/sections/PackingList/utils/isRowEmpty";
import { collectErrorPaths } from "@RequestFormV2/utils/collectErrorPaths";
import { validatePackingListRow } from "@RequestFormV2/validation/sections/packingListFieldRules";

const ROW_FIELD_SUFFIXES = [
  "handling_unit",
  "container_type",
  "units",
  "weight_piece",
  "nmfc_class",
  "additional_field",
  "additional_information",
  "length",
  "width",
  "height",
  "cbm_scope",
  "l_w_h_cbm_piece",
  "l_w_h_cbm_total",
] as const;

export function usePackingListRowReactiveClear(
  index: number,
  row: PackingListRow,
  handlingUnitHashMap: HandlingUnitHashMap,
  nmfcNumberEnabled?: string | null,
) {
  const intl = useIntl();
  const { hasAttemptedReview, clearFieldError, syncErroredSections } = useRequestFormReviewValidation();
  const {
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    if (!hasAttemptedReview) return;

    const rowPrefix = `packing_list.${index}.`;
    const invalidPaths = !isPackingListRowEmpty(row)
      ? new Set(validatePackingListRow(row, index, handlingUnitHashMap, { nmfcNumberEnabled }))
      : new Set<string>();

    const requiredMessage = intl.formatMessage({ id: "general.required", defaultMessage: "Required" });

    const clearPath = (path: string) => {
      if (clearFieldError) {
        clearFieldError(path);
      } else {
        clearErrors(path);
      }
    };

    collectErrorPaths(errors)
      .filter((path) => path.startsWith(rowPrefix))
      .forEach((path) => {
        if (!invalidPaths.has(path)) {
          clearPath(path);
        }
      });

    ROW_FIELD_SUFFIXES.forEach((suffix) => {
      const path = `${rowPrefix}${suffix}`;
      if (get(errors, path) && !invalidPaths.has(path)) {
        clearPath(path);
      }
    });

    let didSetError = false;
    invalidPaths.forEach((path) => {
      if (!get(errors, path)) {
        setError(path, { type: "manual", message: requiredMessage });
        didSetError = true;
      }
    });

    if (didSetError && syncErroredSections) {
      queueMicrotask(() => {
        queueMicrotask(() => {
          syncErroredSections();
        });
      });
    }
  }, [
    row,
    index,
    handlingUnitHashMap,
    nmfcNumberEnabled,
    hasAttemptedReview,
    clearFieldError,
    syncErroredSections,
    errors,
    setError,
    clearErrors,
    intl,
  ]);
}
